import { NgTemplateOutlet } from '@angular/common';
import { Component, ContentChild, TemplateRef, input, output } from '@angular/core';
import { FormControl, ReactiveFormsModule } from '@angular/forms';
import { NgIconComponent, provideIcons } from '@ng-icons/core';
import { lucideCircleAlert } from '@ng-icons/lucide';

/**
 * @description A resusable text input component.
 * This component is responsible for:
 * * Displaying the input and label
 * * Managing correct aria attributes
 * * Displaying the error messages and help text
 */
@Component({
  selector: 'app-text-input',
  imports: [ReactiveFormsModule, NgIconComponent, NgTemplateOutlet],
  providers: [provideIcons({ lucideCircleAlert })],
  templateUrl: './text-input.component.html',
  styles: [
    `
      // Kinetic fix. Can remove after https://github.com/kin/kinetic/issues/478 is fixed
      .input__control:focus-within:not(:disabled) {
        box-shadow: 0 0 0 3px var(--color-primary);
        outline: 1px solid var(--color-neutral-700);
      }
    `,
  ],
})
export class TextInputComponent {
  /**
   * @description Whether the input is read only
   */
  readonly = input(false);
  /**
   * @description The form control for the input
   */
  control = input.required<FormControl>();
  /**
   * @description The label for the input (optional - omit when using external label/legend)
   */
  label = input('');
  /**
   * @description The help text for the input
   */
  helpText = input<string | undefined>();
  /**
   * @description The id for the input (optional - attributes are omitted if not provided)
   */
  id = input('');
  /**
   * @description The type for the input
   */
  type = input('text');
  /**
   * @description The placeholder for the input
   */
  placeholder = input(' ');
  /**
   * @description The pattern for the input
   */
  pattern = input('');
  /**
   * @description The required for the input
   */
  required = input(false);
  /**
   * @description The min date for the input if it is a date input.
   * @note Provide the date in the format 'yyyy-MM-dd', e.g. '2021-01-01'.
   */
  min = input<string | undefined>();
  /**
   * @description The max date for the input if it is a date input
   * @note Provide the date in the format 'yyyy-MM-dd', e.g. '2021-01-01'.
   */
  max = input<string | undefined>();
  /**
   * @description The error messages for the input
   */
  errorMessages = input<{ [key: string]: string }>({});

  /**
   * @description Reflects the inputmode attribute for the input
   */
  inputmode = input<string | undefined>();

  private defaultErrorMessages: { [key: string]: string } = {
    required: 'This field is required',
    email: 'Please enter a valid email address',
    pattern: 'Please enter a valid value',
    minlength: 'This field is too short',
    maxlength: 'This field is too long',
  };

  /**
   * @description The custom error template for the input
   * @slot - (default)
   * @note If this is provided, the error messages passed in via the `errorMessages` input will be ignored.
   */
  @ContentChild(TemplateRef) customErrorTemplate?: TemplateRef<{ $implicit: string | null }>;

  /**
   * @description Returns the error message for the input based on control state.
   * This is a method (not a computed signal) because FormControl's touched/errors
   * properties are not signals, so we need this to evaluate on every change detection.
   */
  errorMessage(): string | null {
    const ctrl = this.control();
    const errMsgs = this.errorMessages();
    if (ctrl.errors && (ctrl.touched || ctrl.dirty)) {
      const errorKey = Object.keys(ctrl.errors)[0];
      return errMsgs[errorKey] || this.defaultErrorMessages[errorKey] || `This field is invalid`;
    }
    return null;
  }

  /**
   * @description Returns the input's id attribute value, or null to omit the attribute.
   */
  inputId(): string | null {
    return this.id() || null;
  }

  /**
   * @description Returns the message container's id attribute value, or null to omit.
   */
  messageId(): string | null {
    const id = this.id();
    return id ? `${id}-message` : null;
  }

  /**
   * @description Returns the error element's id attribute value, or null to omit.
   */
  errorId(): string | null {
    const id = this.id();
    return id ? `${id}-error` : null;
  }

  /**
   * @description Returns the aria-describedby attribute value, or null to omit.
   * Only returns a value when there's content to describe (helpText or errorMessage) AND an id is provided.
   */
  ariaDescribedBy(): string | null {
    const id = this.id();
    if (!id) return null;
    return this.helpText() || this.errorMessage() ? `${id}-message` : null;
  }

  blurred = output<Event>();

  onBlur(event: Event): void {
    this.blurred.emit(event);
  }

  /**
   * Angular's 'updateOn' can only be 'blur' or 'submit' and we kind of need both.
   * For example, we type 01/01/2000 and hit submit. The value is still empty until you leave the field.
   * This method listens for enter and manually triggers blur.
   * @param event The key press event
   */
  onKeyPress(event: KeyboardEvent): void {
    if (event.key === 'Enter') {
      event.preventDefault();
      const input = event.target as HTMLInputElement;
      input.blur();
      this.blurred.emit(event);

      const form = input.closest('form');
      if (form) {
        form.dispatchEvent(new Event('submit', { cancelable: true, bubbles: true }));
      }
    }
  }
}
